import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Plus, Trash2, Loader2, Play, CheckCircle2, Timer, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  useSprints,
  useCreateSprint,
  useStartSprint,
  useCompleteSprint,
  useDeleteSprint,
  type ProjectBacklogItem,
} from "@/hooks/useProjectPlanning";
import { SPRINT_STATUS_META } from "@/lib/statusTone";

export function SprintsPanel({
  clientProjectId,
  items,
}: {
  clientProjectId: string;
  items: ProjectBacklogItem[];
}) {
  const { data: sprints = [], isLoading } = useSprints(clientProjectId);
  const createSprint = useCreateSprint(clientProjectId);
  const startSprint = useStartSprint(clientProjectId);
  const completeSprint = useCompleteSprint(clientProjectId);
  const deleteSprint = useDeleteSprint(clientProjectId);
  const [newName, setNewName] = useState("");
  const [showForm, setShowForm] = useState(false);

  const handleCreate = () => {
    if (!newName.trim()) return;
    createSprint.mutate(
      { name: newName.trim() },
      {
        onSuccess: () => {
          setNewName("");
          setShowForm(false);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {showForm ? (
        <div className="flex gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleCreate();
              }
            }}
            placeholder="Nome da sprint (ex: Sprint 1)"
            autoFocus
          />
          <Button onClick={handleCreate} disabled={createSprint.isPending || !newName.trim()}>
            Criar
          </Button>
          <Button variant="ghost" onClick={() => setShowForm(false)}>
            Cancelar
          </Button>
        </div>
      ) : (
        <Button variant="outline" onClick={() => setShowForm(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> Nova Sprint
        </Button>
      )}

      {sprints.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          <Timer className="mx-auto mb-2 h-8 w-8 opacity-40" />
          Nenhuma sprint criada ainda.
        </div>
      ) : (
        <div className="space-y-2">
          {sprints.map((sprint) => {
            const sprintItems = items.filter((i) => i.sprint_id === sprint.id);
            const totalPoints = sprintItems.reduce((sum, i) => sum + (i.story_points ?? 0), 0);
            const donePoints = sprintItems
              .filter((i) => i.status === "done")
              .reduce((sum, i) => sum + (i.story_points ?? 0), 0);
            const progress = totalPoints > 0 ? Math.round((donePoints / totalPoints) * 100) : 0;
            return (
              <Card key={sprint.id}>
                <Collapsible defaultOpen={sprint.status === "active"}>
                  <CardContent className="space-y-2 p-3">
                    <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 text-left">
                      <div className="flex min-w-0 items-center gap-2">
                        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <p className="truncate text-sm font-medium">{sprint.name}</p>
                        <StatusBadge tone={SPRINT_STATUS_META[sprint.status].tone}>
                          {SPRINT_STATUS_META[sprint.status].label}
                        </StatusBadge>
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {sprintItems.length} itens
                      </span>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-2 pt-1">
                      {sprint.goal && (
                        <p className="text-xs text-muted-foreground">{sprint.goal}</p>
                      )}
                      {(sprint.start_date || sprint.end_date) && (
                        <p className="text-xs text-muted-foreground">
                          {sprint.start_date &&
                            format(new Date(sprint.start_date), "dd/MM/yy", { locale: ptBR })}
                          {sprint.start_date && sprint.end_date && " – "}
                          {sprint.end_date &&
                            format(new Date(sprint.end_date), "dd/MM/yy", { locale: ptBR })}
                        </p>
                      )}
                      {totalPoints > 0 && (
                        <div className="flex items-center gap-2">
                          <Progress value={progress} className="h-1.5 flex-1" />
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {donePoints}/{totalPoints} pts
                          </span>
                        </div>
                      )}
                      <div className="flex gap-1.5 pt-1">
                        {sprint.status === "planned" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => startSprint.mutate(sprint.id)}
                          >
                            <Play className="mr-1 h-3 w-3" /> Iniciar
                          </Button>
                        )}
                        {sprint.status === "active" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => completeSprint.mutate(sprint.id)}
                          >
                            <CheckCircle2 className="mr-1 h-3 w-3" /> Concluir
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="ml-auto h-7 text-xs text-destructive"
                          onClick={() => deleteSprint.mutate(sprint.id)}
                        >
                          <Trash2 className="mr-1 h-3 w-3" /> Remover
                        </Button>
                      </div>
                    </CollapsibleContent>
                  </CardContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
